import { configureStore } from '@reduxjs/toolkit';
import { contentApi } from '../api/content.api';
import { setSessionEndedHandler } from '../auth/authClient';
import { authReducer, sessionEnded } from './authSlice';

// A factory rather than a bare singleton, so tests build a store per case
// instead of leaking state between them. The app still uses exactly one — see
// the export below.
//
// **Two things live in the store and they are not the same kind of thing.**
// `auth` is client state the app owns: a status, a user, an error. `contentApi`
// is a cache of server state the app does not own, managed by RTK Query. That
// split is the rule — a page's server data goes through an endpoint, never into
// a hand-written slice.
export const createStore = () =>
  configureStore({
    reducer: {
      auth: authReducer,
      [contentApi.reducerPath]: contentApi.reducer,
    },
    // Without this the cache never refetches, never expires an unused entry and
    // never polls: the reducer alone is inert. Concatenated rather than
    // replacing the defaults, which is what keeps the serialisability and
    // immutability checks on in development.
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(contentApi.middleware),
  });

export const store = createStore();

// Closes the loop the auth client deliberately left open: it fires when silent
// renew fails or the token expires, and only the store can turn that into
// application state. Registered here because the client cannot import the store
// without a cycle.
setSessionEndedHandler(() => {
  store.dispatch(sessionEnded());
});

export type AppStore = ReturnType<typeof createStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];
